import { useState } from 'react';
import dynamic from 'next/dynamic';

import clsx from 'clsx';
import { Emoji } from 'emoji-mart';

import useTranslation from '@/utils/hooks/translation/useTranslation';
import useActiveSocialProfile from '@/utils/hooks/user/useActiveSocialProfile';

import { addEmoji } from '@/utils/addEmoji';
import { useIsElementFocused } from '@/utils/hooks/utils/useIsElementFocused';
import { useValidateCaption, ValidateReturnType } from '@/utils/hooks/utils/useValidateCaption';

import type {
    CaptionLayoutConfig,
    CaptionRestriction,
    CaptionWrapperStyle,
    SocialProfile,
    ValidationResult
} from '@/components/composite/Caption/types';
import { CAPTION_TYPE, HASHTAG_LIST_TYPE } from '@/lib/captionsHashtagLists/types';

import {
    CaptionRestrictionsType,
    CaptionType
} from '@/lib/captionsHashtagLists/captionRestrictions';

import { DefaultAvatar as Avatar } from '@/components/base/Avatar';
import { Caption } from '@/components/composite/Caption';
import { Trans } from '@/components/tools/translation';

const EmojiPicker = dynamic(() => import('@/components/base/EmojiPicker'));

type XCaptionProps = {
    captionLayoutConfig?: CaptionLayoutConfig;
    valid?: ValidationResult;
    caption: CaptionType;
    updateCaption: (caption: string) => void;
    validateCaptionCallback?: (caption: ValidateReturnType) => void;
    initialRestrictions?: CaptionRestrictionsType[];
    captionWrapperStyle?: CaptionWrapperStyle;
    footerPortalContainer?: HTMLElement;
    inputProps?: Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, 'style'>;
    rows?: number;
    forcedSocialProfile?: SocialProfile | null;
    rootId?: string; // wrapper element when the eventListener will be added to handle focus style of the caption
};

export const XCaption = ({
    captionLayoutConfig = { hasBorder: true, hasAvatar: true, label: null, aiButton: false },
    valid,
    caption,
    updateCaption,
    validateCaptionCallback,
    initialRestrictions,
    captionWrapperStyle = 'default',
    footerPortalContainer,
    inputProps,
    rows = 8,
    forcedSocialProfile = null,
    rootId
}: XCaptionProps) => {
    const { t } = useTranslation();
    const { activeSocialProfile } = useActiveSocialProfile();
    const [cursorPosition, setCursorPosition] = useState<number>(0);
    const [captionRestrictions, setCaptionRestrictions] = useState<
        CaptionRestriction[] | undefined
    >(initialRestrictions);
    const { wrapperRef, isFocused, setIsFocused } = useIsElementFocused(rootId);

    const captionRef = wrapperRef as React.MutableRefObject<HTMLDivElement>;

    useValidateCaption(
        caption,
        initialRestrictions,
        validateCaptionCallback,
        setCaptionRestrictions
    );

    const handleBlur = (e: React.FocusEvent<HTMLTextAreaElement>) => {
        setCursorPosition(e.target.selectionStart ?? 0);
        inputProps?.onBlur?.(e);
    };

    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        updateCaption(e.target.value);
        inputProps?.onChange?.(e);
    };

    const handleFocus = (e: React.FocusEvent<HTMLTextAreaElement>) => {
        setIsFocused(true);
        inputProps?.onFocus?.(e);
    };

    const handleEmojiSelect = (emoji: typeof Emoji.Props) => {
        addEmoji(emoji, caption, updateCaption, cursorPosition, setCursorPosition);
    };

    return (
        <Caption captionWrapperStyle={captionWrapperStyle} className="space-y-4" ref={captionRef}>
            <Caption.Content className="items-start">
                {captionLayoutConfig?.hasAvatar ? (
                    <Avatar
                        url={
                            (forcedSocialProfile ?? activeSocialProfile)?.external_api_data
                                ?.avatar ?? null
                        }
                        fallback={
                            (forcedSocialProfile ?? activeSocialProfile)?.slug?.charAt(0) ?? 'U'
                        }
                        profileType={
                            (forcedSocialProfile ?? activeSocialProfile)?.social_network_type
                        }
                        className="mr-5"
                    />
                ) : null}
                <Caption.TextArea
                    {...inputProps}
                    onBlur={handleBlur}
                    value={caption}
                    onChange={handleChange}
                    onFocus={handleFocus}
                    rows={rows}
                    className="flex-1 leading-5"
                />
                {captionLayoutConfig?.hasBorder ? (
                    <Caption.Border
                        className={clsx({
                            'ring-icoGray-400 dark:ring-icoDarkMode-beton': !isFocused,
                            'ring-icoBlue': isFocused && valid?.valid,
                            'ring-icoYellow': isFocused && valid?.valid && valid?.warning,
                            'ring-icoRed': !valid?.valid
                        })}
                    />
                ) : null}
            </Caption.Content>
            <Caption.Footer>
                <Caption.Restrictions
                    className="flex-1"
                    valid={valid}
                    content={
                        <>
                            {captionRestrictions?.map(({ type, restriction }, id) => {
                                return (
                                    <span key={type}>
                                        <Trans
                                            i18nKey={`commons:caption_restrictions.${type}`}
                                            values={{
                                                [type]: restriction?.limit
                                            }}
                                            components={{
                                                tag0: <span className="font-GSemiBold" />
                                            }}
                                        />
                                        {id + 1 !== captionRestrictions.length ? ', ' : null}
                                    </span>
                                );
                            })}
                        </>
                    }
                />
                <div className="flex divide-x divide-icoGray-300 dark:divide-icoDarkMode-beton">
                    <div className="flex gap-5 pr-5">
                        <Caption.Popover
                            type={HASHTAG_LIST_TYPE}
                            currentCaption={caption}
                            updateCaption={updateCaption}
                            forcedSocialProfile={forcedSocialProfile}
                            buttonTitle={t('publishing:captions_and_lists.hashtag_list')}
                            portalContainer={footerPortalContainer}
                        />
                        <Caption.Popover
                            type={CAPTION_TYPE}
                            currentCaption={caption}
                            updateCaption={updateCaption}
                            forcedSocialProfile={forcedSocialProfile}
                            buttonTitle={t('publishing:captions_and_lists.popover.caption.title')}
                            portalContainer={footerPortalContainer}
                        />
                    </div>
                    <EmojiPicker
                        placement="bottom-end"
                        offset={[5, 10]}
                        selectEmoji={handleEmojiSelect}
                        className="ml-5 text-xl"
                        portalContainer={footerPortalContainer}
                    />
                </div>
            </Caption.Footer>
        </Caption>
    );
};
